import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  TextField,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchHomePageInfo } from 'stores/sensor/homePageSlice';
import { brandColors } from 'types/colors';

const columns = ['comfort_level_avg', 'temperature_avg', 'rh_avg'];
const STREAK_MAX_GAP_SECONDS = 11 * 60;
const OCCUPANCY_FILTERS = [
  { key: 'ALL', label: 'CAV' },
  { key: 'OCCUPIED', label: 'Occupied' },
];

const ComfortEventsModal = ({ buildingId, floorNumber, timeRange }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [expandedSensorId, setExpandedSensorId] = useState(null);
  const [expandedStreakRows, setExpandedStreakRows] = useState({});
  const [query, setQuery] = useState('');
  const [occupancyFilter, setOccupancyFilter] = useState('OCCUPIED');

  const formatUnixTime = unixTime => {
    if (!unixTime) return '—';
    const date = new Date(unixTime * 1000);
    const pad = val => String(val).padStart(2, '0');
    const dd = pad(date.getDate());
    const mm = pad(date.getMonth() + 1);
    const yyyy = date.getFullYear();
    const hh = pad(date.getHours());
    const min = pad(date.getMinutes());
    return `${hh}:${min}, ${dd}.${mm}.${yyyy}`;
  };

  const formatTotalTimeDuration = recordCount => {
    if (!recordCount) return '—';
    const hours = (recordCount * 5) / 60;
    return `${Number(hours.toFixed(1))} h`;
  };

  const formatLongestDeviationDuration = recordCount => {
    if (!recordCount) return '—';
    const hours = (recordCount * 5) / 60;
    return `${Number(hours.toFixed(1))} h`;
  };

  useEffect(() => {
    if (!buildingId || buildingId === 'all') return;
    let isMounted = true;
    setLoading(true);
    setError(null);

    dispatch(
      fetchHomePageInfo({
        building_id: buildingId,
        floor_number: floorNumber,
        timeRange,
        columns,
      }),
    )
      .unwrap()
      .then(result => {
        if (!isMounted) return;
        setData(Array.isArray(result) ? result : []);
      })
      .catch(err => {
        if (!isMounted) return;
        setError(err?.message || 'Failed to load ventilation info');
        setData([]);
      })
      .finally(() => {
        if (!isMounted) return;
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [dispatch, buildingId, floorNumber, timeRange]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const sensorsWithFilteredRecords = data.map(sensor => ({
      ...sensor,
      records:
        occupancyFilter === 'OCCUPIED'
          ? (sensor.records || []).filter(record => Number(record?.occupancy) > 5)
          : sensor.records || [],
    }));
    const sorted = [...sensorsWithFilteredRecords].sort((a, b) => {
      const aFloor = Number(a.location?.floor_number);
      const bFloor = Number(b.location?.floor_number);
      const safeAFloor = Number.isFinite(aFloor) ? aFloor : Number.POSITIVE_INFINITY;
      const safeBFloor = Number.isFinite(bFloor) ? bFloor : Number.POSITIVE_INFINITY;
      if (safeAFloor !== safeBFloor) return safeAFloor - safeBFloor;

      const aName = (a.sensor_name || '').toLowerCase();
      const bName = (b.sensor_name || '').toLowerCase();
      return aName.localeCompare(bName);
    });
    if (!q) return sorted;
    return sorted.filter(item => {
      const haystack = [
        item.sensor_name,
        item.location?.building?.name,
        item.location?.area?.name,
        String(item.location?.floor_number ?? ''),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [data, query, occupancyFilter]);

  const toggleExpanded = sensorId => {
    setExpandedSensorId(prev => (prev === sensorId ? null : sensorId));
  };

  const toggleStreakExpanded = streakKey => {
    setExpandedStreakRows(prev => ({
      ...prev,
      [streakKey]: !prev[streakKey],
    }));
  };

  const getRecordStreaks = records => {
    const sortedRecords = (records || [])
      .filter(record => Number.isFinite(Number(record?.unix_time)))
      .sort((a, b) => Number(a.unix_time) - Number(b.unix_time));

    if (sortedRecords.length === 0) return [];

    const streaks = [];
    let currentRecords = [sortedRecords[0]];

    for (let i = 1; i < sortedRecords.length; i += 1) {
      const prevUnix = Number(sortedRecords[i - 1].unix_time);
      const currUnix = Number(sortedRecords[i].unix_time);
      const gapInSeconds = currUnix - prevUnix;

      if (gapInSeconds <= STREAK_MAX_GAP_SECONDS) {
        currentRecords.push(sortedRecords[i]);
      } else {
        streaks.push(currentRecords);
        currentRecords = [sortedRecords[i]];
      }
    }

    streaks.push(currentRecords);

    return streaks.map(streakRecords => ({
      records: streakRecords,
      startUnix: Number(streakRecords[0].unix_time),
      endUnix: Number(streakRecords[streakRecords.length - 1].unix_time),
    }));
  };

  const getEventGroups = records => {
    const recordsWithEvents = (records || []).filter(record => Number.isFinite(Number(record?.event_start_unix)));

    if (recordsWithEvents.length === 0) return getRecordStreaks(records);

    const eventsByKey = new Map();
    recordsWithEvents.forEach(record => {
      const eventId = Number(record?.event_id ?? 0);
      const startUnix = Number(record.event_start_unix);
      const endUnix = Number(record.event_end_unix ?? record.event_start_unix);
      const eventKey = `${eventId}-${startUnix}-${endUnix}`;

      if (!eventsByKey.has(eventKey)) {
        eventsByKey.set(eventKey, {
          eventId,
          startUnix,
          endUnix,
          records: [],
        });
      }
      eventsByKey.get(eventKey).records.push(record);
    });

    return [...eventsByKey.values()]
      .map(event => ({
        ...event,
        records: event.records.sort((a, b) => Number(a.unix_time) - Number(b.unix_time)),
      }))
      .sort((a, b) => a.startUnix - b.startUnix);
  };

  const getLongestDeviationDetails = records => {
    const streaks = getRecordStreaks(records);
    if (!Array.isArray(streaks) || streaks.length === 0) {
      return { length: 0, startUnix: null, endUnix: null };
    }

    const longestStreak = streaks.reduce((maxStreak, streak) => {
      const maxLength = Number(maxStreak?.records?.length ?? 0);
      const currentLength = Number(streak?.records?.length ?? 0);
      if (currentLength > maxLength) return streak;
      return maxStreak;
    }, null);

    return {
      length: Number(longestStreak?.records?.length ?? 0),
      startUnix: Number(longestStreak?.startUnix ?? 0) || null,
      endUnix: Number(longestStreak?.endUnix ?? 0) || null,
    };
  };

  const getAverageValue = (records, key) => {
    const values = (records || []).map(record => Number(record?.[key])).filter(Number.isFinite);
    if (values.length === 0) return null;
    const sum = values.reduce((acc, value) => acc + value, 0);
    return sum / values.length;
  };

  const formatAvg = value => {
    if (!Number.isFinite(value)) return '—';
    return Number(value.toFixed(1));
  };

  const formatAvgWithUnit = (value, unit) => {
    const formatted = formatAvg(value);
    return formatted === '—' ? '—' : `${formatted}${unit}`;
  };

  const formatStreakSummaryValue = (value, unit, isAverageStreak) => {
    const formatted = formatAvgWithUnit(value, unit);
    if (formatted === '—') return '—';
    return isAverageStreak ? `≈ ${formatted}` : formatted;
  };

  const formatStreakDuration = streak => {
    const recordCount = streak?.records?.length || 0;
    if (recordCount === 0) return '—';
    const totalHours = (recordCount * 5) / 60;
    return `${Number(totalHours.toFixed(1))}h`;
  };

  if (loading) {
    return (
      <Box
        sx={{
          minWidth: 320,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
        }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minWidth: 320, p: 2 }}>
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', p: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Typography sx={{ color: 'text.secondary' }}>{filtered.length} sensors</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {OCCUPANCY_FILTERS.map(filter => {
            const isSelected = occupancyFilter === filter.key;
            return (
              <Button
                key={filter.key}
                variant="inherit"
                disableElevation
                disableRipple
                disableFocusRipple
                onClick={() => setOccupancyFilter(filter.key)}
                aria-pressed={isSelected}
                sx={{
                  borderRadius: '999px',
                  textTransform: 'none',
                  backgroundColor: isSelected ? brandColors.BLUE : brandColors.BLUE_LIGHT,
                  color: isSelected ? '#fff' : brandColors.BLUE,
                  minHeight: 32,
                  px: 2,
                  '&:hover': {
                    backgroundColor: isSelected ? brandColors.BLUE : brandColors.BLUE_LIGHT,
                    opacity: 0.9,
                  },
                  '&:active': {
                    backgroundColor: isSelected ? brandColors.BLUE : brandColors.BLUE_LIGHT,
                  },
                  '&.Mui-focusVisible': {
                    boxShadow: 'none',
                  },
                }}>
                {filter.label}
              </Button>
            );
          })}
        </Box>
        <Box sx={{ flex: 1 }} />
        <TextField
          size="small"
          placeholder="Search sensor, area, floor, mac..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          sx={{ width: '320px' }}
        />
      </Stack>

      <TableContainer
        sx={{
          width: '100%',
          maxHeight: '60vh',
          overflowX: { xs: 'auto', lg: 'hidden' },
          overflowY: 'auto',
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          boxShadow: 'none',
        }}>
        <Table
          stickyHeader
          size="small"
          sx={{
            tableLayout: 'fixed',
            borderCollapse: 'collapse',
            width: '100%',
            '& thead th': {
              textAlign: 'center',
              height: 'auto',
              minHeight: 56,
              maxHeight: '2.4em',
              lineHeight: 1.2,
              verticalAlign: 'middle',
              whiteSpace: 'normal',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              writingMode: 'horizontal-tb',
            },
          }}>
          {/* IMPORTANT: Define all widths here. Remove widths from TableCell props below. */}
          <colgroup>
            <col style={{ width: '22%' }} />
            <col style={{ width: '22%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '24%' }} />
            <col style={{ width: '8%' }} />
          </colgroup>

          <TableHead
            sx={{
              backgroundColor: '#f9fafb',
              display: 'table-header-group',
              p: 0,
              '& th': {
                position: 'sticky',
                top: 0,
                zIndex: 3,
                backgroundColor: '#f9fafb',
              },
            }}>
            <TableRow sx={{ minHeight: 56, '& > th': { py: 0.5 } }}>
              {/* align="center" plus textAlign: 'center' ensures perfect horizontal centering */}
              <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0', fontWeight: 700, textAlign: 'center' }}>
                Sensor
              </TableCell>
              <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0', fontWeight: 700, textAlign: 'center' }}>
                Location
              </TableCell>
              <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0', fontWeight: 700, textAlign: 'center' }}>
                Events
              </TableCell>
              <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0', fontWeight: 700, textAlign: 'center' }}>
                Total Time
              </TableCell>
              <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0', fontWeight: 700, textAlign: 'center' }}>
                Longest Deviation
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, textAlign: 'center' }}>
                Expand
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filtered.map(sensor => {
              const records = sensor.records || [];
              const events = getEventGroups(records);
              const eventCount = events.length;
              const longestDeviation = events.reduce(
                (longest, event) => (Number(event.records?.length ?? 0) > Number(longest.records?.length ?? 0) ? event : longest),
                { records: [] },
              );
              const locationText = `Floor ${
                sensor.location?.floor_number ?? 'N/A'
              } • ${sensor.location?.area?.name || 'Unassigned'}`;

              return (
                <React.Fragment key={sensor.id}>
                  <TableRow hover sx={{ height: 48, '& > td': { py: 0 } }}>
                    <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0', textAlign: 'start' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        <Box
                          component="span"
                          onClick={() => navigate(`/data-explorer/${sensor.id}`)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { opacity: 0.85 },
                          }}>
                          {sensor.sensor_name}
                        </Box>
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        borderRight: '1px solid #e0e0e0',
                        textAlign: 'start',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>
                      {locationText}
                    </TableCell>
                    <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0', textAlign: 'center' }}>
                      {eventCount}
                    </TableCell>
                    <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0', textAlign: 'center' }}>
                      {formatTotalTimeDuration(records.length)}
                    </TableCell>
                    <TableCell align="center" sx={{ borderRight: '1px solid #e0e0e0', textAlign: 'center' }}>
                      {formatLongestDeviationDuration(longestDeviation.records?.length)}
                    </TableCell>
                    <TableCell align="center" sx={{ textAlign: 'center' }}>
                      <IconButton disabled={records.length === 0} size="small" onClick={() => toggleExpanded(sensor.id)}>
                        {records.length === 0 ? (
                          '—'
                        ) : expandedSensorId === sensor.id ? (
                          <KeyboardArrowUpIcon />
                        ) : (
                          <KeyboardArrowDownIcon color="primary" />
                        )}
                      </IconButton>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    {/* CRITICAL: colSpan must be exactly 6 to match the colgroup/header structure */}
                    <TableCell colSpan={6} sx={{ p: 0, borderBottom: 'none' }}>
                      <Collapse in={expandedSensorId === sensor.id} timeout="auto" unmountOnExit>
                        <Box
                          sx={{
                            py: 3,
                            px: 0,
                            backgroundColor: '#f7f8fb',
                            borderTop: '1px solid #e0e0e0',
                          }}>
                          {records.length === 0 ? (
                            <Typography variant="body2" sx={{ color: 'text.secondary', px: 3 }}>
                              No records with `co2_ratio` &lt; 70 for this time range.
                            </Typography>
                          ) : (
                            <Table
                              stickyHeader
                              size="small"
                              sx={{
                                width: '100%',
                                tableLayout: 'fixed',
                                '& thead th': {
                                  textAlign: 'center',
                                  height: 'auto',
                                  minHeight: 56,
                                  maxHeight: '2.4em',
                                  lineHeight: 1.2,
                                  verticalAlign: 'middle',
                                  whiteSpace: 'normal',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  writingMode: 'horizontal-tb',
                                },
                              }}>
                              <colgroup>
                                <col style={{ width: '20%' }} />
                                <col style={{ width: '10%' }} />
                                <col style={{ width: '14%' }} />
                                <col style={{ width: '14%' }} />
                                <col style={{ width: '14%' }} />
                                <col style={{ width: '14%' }} />
                                <col style={{ width: '14%' }} />
                              </colgroup>
                              <TableHead
                                sx={{
                                  display: 'table-header-group',
                                  p: 0,
                                  '& th': {
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 3,
                                    backgroundColor: '#f7f8fb',
                                  },
                                }}>
                                <TableRow sx={{ minHeight: 56, '& > th': { py: 0.5 } }}>
                                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                                    Time
                                  </TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                                    Duration
                                  </TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                                    Occupancy (avg)
                                  </TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                                    Comfort Level (avg)
                                  </TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                                    Temperature (avg)
                                  </TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                                    Relative Humidity (avg)
                                  </TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                                    Expand
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {events.map((streak, streakIdx) => {
                                  const streakKey = `${sensor.id}-${streak.startUnix}-${streak.endUnix}-${streakIdx}`;
                                  const isMultiRecordStreak = streak.records.length > 1;
                                  const isExpanded = Boolean(expandedStreakRows[streakKey]);
                                  const avgOccupancy = getAverageValue(streak.records, 'occupancy');
                                  const avgComfortLevel = getAverageValue(streak.records, 'comfort_level_avg');
                                  const avgTemperature = getAverageValue(streak.records, 'temperature_avg');
                                  const avgRh = getAverageValue(streak.records, 'rh_avg');
                                  return (
                                    <React.Fragment key={streakKey}>
                                      <TableRow sx={{ height: 44, '& > td': { py: 0 } }}>
                                        <TableCell align="center">
                                          <Typography variant="body2" sx={{ fontWeight: isExpanded ? 700 : 400 }}>
                                            {isMultiRecordStreak
                                              ? `${formatUnixTime(streak.startUnix)} → ${formatUnixTime(streak.endUnix)}`
                                              : formatUnixTime(streak.startUnix)}
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="center">{formatStreakDuration(streak)}</TableCell>
                                        <TableCell align="center">
                                          {formatStreakSummaryValue(avgOccupancy, '%', isMultiRecordStreak)}
                                        </TableCell>
                                        <TableCell align="center">
                                          {formatStreakSummaryValue(avgComfortLevel, '%', isMultiRecordStreak)}
                                        </TableCell>
                                        <TableCell align="center">
                                          {formatStreakSummaryValue(avgTemperature, '℃', isMultiRecordStreak)}
                                        </TableCell>
                                        <TableCell align="center">
                                          {formatStreakSummaryValue(avgRh, '%', isMultiRecordStreak)}
                                        </TableCell>
                                        <TableCell align="center">
                                          {isMultiRecordStreak ? (
                                            <IconButton size="small" onClick={() => toggleStreakExpanded(streakKey)}>
                                              {isExpanded ? (
                                                <KeyboardArrowUpIcon fontSize="small" />
                                              ) : (
                                                <KeyboardArrowDownIcon fontSize="small" color="primary" />
                                              )}
                                            </IconButton>
                                          ) : (
                                            '—'
                                          )}
                                        </TableCell>
                                      </TableRow>
                                      {isMultiRecordStreak && (
                                        <TableRow>
                                          <TableCell colSpan={7} sx={{ p: 0, borderBottom: 'none' }}>
                                            <Collapse in={isExpanded} timeout={220} unmountOnExit>
                                              <Box sx={{ backgroundColor: '#dbe6ff', px: 0 }}>
                                                <Table
                                                  stickyHeader
                                                  size="small"
                                                  sx={{
                                                    width: '100%',
                                                    tableLayout: 'fixed',
                                                    borderCollapse: 'collapse',
                                                    '& thead th': {
                                                      textAlign: 'center',
                                                      height: 'auto',
                                                      minHeight: 56,
                                                      maxHeight: '2.4em',
                                                      lineHeight: 1.2,
                                                      verticalAlign: 'middle',
                                                      whiteSpace: 'normal',
                                                      overflow: 'hidden',
                                                      textOverflow: 'ellipsis',
                                                      writingMode: 'horizontal-tb',
                                                    },
                                                    '& tbody td': { textAlign: 'center' },
                                                  }}>
                                                  <colgroup>
                                                    <col style={{ width: '26%' }} />
                                                    <col style={{ width: '14%' }} />
                                                    <col style={{ width: '14%' }} />
                                                    <col style={{ width: '14%' }} />
                                                    <col style={{ width: '14%' }} />
                                                    <col style={{ width: '10%' }} />
                                                    <col style={{ width: '8%' }} />
                                                  </colgroup>
                                                  <TableHead
                                                    sx={{
                                                      display: 'table-header-group',
                                                      p: 0,
                                                      '& th': {
                                                        position: 'sticky',
                                                        top: 0,
                                                        zIndex: 3,
                                                        backgroundColor: '#dbe6ff',
                                                      },
                                                    }}>
                                                    <TableRow
                                                      sx={{
                                                        height: 56,
                                                        '& > th': { py: 0, fontWeight: 600 },
                                                      }}>
                                                      <TableCell align="center">Time</TableCell>
                                                      <TableCell align="center">Occupancy</TableCell>
                                                      <TableCell align="center">Comfort Level</TableCell>
                                                      <TableCell align="center">Temperature</TableCell>
                                                      <TableCell align="center">Relative Humidity</TableCell>
                                                      <TableCell align="center"></TableCell>
                                                      <TableCell align="center"></TableCell>
                                                    </TableRow>
                                                  </TableHead>
                                                  <TableBody>
                                                    {streak.records.map((rec, recIdx) => (
                                                      <TableRow
                                                        key={`${streakKey}-rec-${rec.unix_time}-${recIdx}`}
                                                        sx={{ height: 40, '& > td': { py: 0 } }}>
                                                        <TableCell align="center">{formatUnixTime(rec.unix_time)}</TableCell>
                                                        <TableCell align="center">
                                                          {Number.isFinite(Number(rec.occupancy)) ? `${rec.occupancy}%` : '—'}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                          {Number.isFinite(Number(rec.comfort_level_avg))
                                                            ? `${rec.comfort_level_avg}%`
                                                            : '—'}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                          {Number.isFinite(Number(rec.temperature_avg))
                                                            ? `${rec.temperature_avg}℃`
                                                            : '—'}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                          {Number.isFinite(Number(rec.rh_avg)) ? `${rec.rh_avg}%` : '—'}
                                                        </TableCell>
                                                        <TableCell align="center"></TableCell>
                                                        <TableCell align="center"></TableCell>
                                                      </TableRow>
                                                    ))}
                                                  </TableBody>
                                                </Table>
                                              </Box>
                                            </Collapse>
                                          </TableCell>
                                        </TableRow>
                                      )}
                                    </React.Fragment>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ComfortEventsModal;
